import axios from "axios"
import { api, readCookie } from "@/lib/api"
import { useAuthStore, type Role, type AuthUser } from "@/stores/auth"

// Synchronous "a session probably exists" hint — auth_status is the non-httpOnly
// companion cookie to the httpOnly auth_token, readable via document.cookie.
export function hasAuthSessionHint(): boolean {
  return readCookie("auth_status") === "1"
}

// One-shot bootstrap on app load (S6 5b/A5). Under httpOnly cookies a hard
// refresh can't read the JWT in JS, so we reconstruct identity from the cookie
// session via GET /auth/me. Distinguishes a real 401 (clear everything) from a
// transient/network error (keep whatever state we have; never force-logout on a
// blip).
export async function bootstrapAuth(): Promise<void> {
  const store = useAuthStore.getState()
  if (!hasAuthSessionHint()) {
    store.setBootstrapped(true) // genuinely logged out — nothing to fetch
    return
  }
  try {
    const res = await api.get("/auth/me")
    const d = res.data.data as { role: Role; user: AuthUser; isImpersonation?: boolean }
    store.hydrateFromMe(d) // sets role/user/isImpersonating + bootstrapped:true
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      // Real: the session is invalid → wipe everything. clear() also sets
      // bootstrapped:true; the api 401 interceptor may also fire clear() — idempotent.
      store.clear()
    } else {
      // Transient/network/offline: do NOT nuke a possibly-valid session. Keep
      // whatever the localStorage seed gave us; just stop gating so the app renders.
      store.setBootstrapped(true)
    }
  }
}
